import { useState, useEffect } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Send, Loader2, CheckCircle } from "lucide-react";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Contact Us - Get Free Quote | Amar Preet Scaffolding";
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    if (!formData.name.trim()) { setError("Please enter your name."); setLoading(false); return; }
    if (!formData.phone.trim() || formData.phone.trim().length < 5) { setError("Please enter a valid phone number."); setLoading(false); return; }
    if (!formData.subject) { setError("Please select a subject."); setLoading(false); return; }
    if (!formData.message.trim()) { setError("Please enter your message."); setLoading(false); return; }

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        source_page: window.location.pathname
      });

      setSuccess(true);
      setFormData({
        name: "",
        phone: "",
        email: "",
        subject: "",
        message: ""
      });
    } catch (err) {
      if (err.response && err.response.data && err.response.data.detail) {
        const details = err.response.data.detail;
        if (Array.isArray(details)) {
          setError(details.map(d => d.msg).join(". "));
        } else {
          setError(String(details));
        }
      } else {
        setError("Failed to submit form. Please try again or call us directly at +91 9355744699.");
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO title="Contact Us" description="Get a free scaffolding rental quote from Amar Preet. Call +91 9355744699 or fill our form. Quick response within 24 hours. Delhi, Noida, Hyderabad, Vizag." canonical="/contact" />
      {/* Hero */}
      <section className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6" data-testid="contact-headline">Contact Us</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Get in touch with us for all your scaffolding needs
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-6">Get In Touch</h3>
                <p className="text-slate-600 leading-relaxed">
                  Have a question or need a quote? Fill out the form and we'll get back to you within 24 hours.
                </p>
              </div>

              <div className="space-y-6">
                <a href="tel:+919355744699" className="flex items-start space-x-4 group" data-testid="contact-phone">
                  <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-slate-900" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 mb-1">Phone</p>
                    <p className="text-slate-600 group-hover:text-yellow-600 transition-colors">+91 9355744699</p>
                  </div>
                </a>

                <a href="mailto:info@amarpreet.in" className="flex items-start space-x-4 group" data-testid="contact-email">
                  <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-slate-900" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 mb-1">Email</p>
                    <p className="text-slate-600 group-hover:text-yellow-600 transition-colors">info@amarpreet.in</p>
                  </div>
                </a>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-slate-900" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 mb-1">Locations</p>
                    <p className="text-slate-600">Delhi, Noida, Hyderabad, Vizag</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-slate-900" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 mb-1">Business Hours</p>
                    <p className="text-slate-600">Mon - Sat: 9:00 AM - 6:00 PM</p>
                    <p className="text-slate-600">Sunday: Closed</p>
                  </div>
                </div>
              </div>

              {/* WhatsApp CTA */}
              <div className="pt-6">
                <a
                  href="https://wa.me/919355744699?text=Hi%2C%20I%27m%20interested%20in%20scaffolding%20rental%20services."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center space-x-3 bg-green-500 hover:bg-green-600 text-white px-6 py-4 rounded-lg font-semibold transition-all duration-300"
                  data-testid="contact-whatsapp"
                >
                  <svg viewBox="0 0 32 32" className="w-6 h-6 fill-white">
                    <path d="M16.004 0h-.008C7.174 0 0 7.176 0 16c0 3.5 1.128 6.744 3.046 9.378L1.054 31.386l6.184-1.962A15.89 15.89 0 0016.004 32C24.826 32 32 24.822 32 16S24.826 0 16.004 0zm9.306 22.614c-.39 1.1-1.932 2.012-3.188 2.278-.86.182-1.982.328-5.76-1.238-4.836-2.004-7.946-6.912-8.186-7.232-.232-.32-1.942-2.586-1.942-4.932s1.228-3.498 1.666-3.978c.39-.428 1.022-.626 1.628-.626.196 0 .372.01.53.018.438.018.658.044 .948.734.362.858 1.244 3.038 1.354 3.26.112.22.186.48.038.76-.14.29-.212.468-.416.72-.206.252-.432.562-.618.754-.206.212-.42.442-.18.866.24.424 1.066 1.754 2.29 2.842 1.576 1.4 2.902 1.834 3.314 2.038.32.158.702.138.95-.12.316-.328.706-.872 1.102-1.408.282-.382.636-.428.99-.29.358.13 2.27 1.07 2.66 1.266.39.196.65.29.746.456.096.166.096.962-.294 2.062z"/>
                  </svg>
                  <span>Chat on WhatsApp</span>
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="bg-slate-50 p-8 rounded-2xl space-y-6" data-testid="contact-form">
                {success && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg flex items-center space-x-3" data-testid="form-success">
                    <CheckCircle className="w-5 h-5" />
                    <div>
                      <p className="font-semibold">Thank you for contacting us!</p>
                      <p className="text-sm">We'll get back to you within 24 hours.</p>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg" data-testid="form-error">
                    {error}
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="text-sm font-medium text-slate-900">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your name"
                      className="mt-2 w-full h-12 px-4 rounded-lg border border-slate-200 bg-white focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/20 transition-all"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="text-sm font-medium text-slate-900">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+91 XXXXX XXXXX"
                      className="mt-2 w-full h-12 px-4 rounded-lg border border-slate-200 bg-white focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/20 transition-all"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="text-sm font-medium text-slate-900">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@company.com"
                      className="mt-2 w-full h-12 px-4 rounded-lg border border-slate-200 bg-white focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/20 transition-all"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="text-sm font-medium text-slate-900">
                      Subject *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="mt-2 w-full h-12 px-4 rounded-lg border border-slate-200 bg-white focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/20 transition-all"
                      data-testid="input-subject"
                    >
                      <option value="">Select subject</option>
                      <option value="Request Quote">Request Quote</option>
                      <option value="Project Inquiry">Project Inquiry</option>
                      <option value="Support">Support</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="text-sm font-medium text-slate-900">
                    Your Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Tell us about your requirements..."
                    className="mt-2 w-full px-4 py-3 rounded-lg border border-slate-200 bg-white focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/20 transition-all resize-none"
                    data-testid="input-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-14 bg-yellow-500 hover:bg-yellow-600 text-slate-900 font-semibold text-lg rounded-lg transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
